'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useI18n } from '@udemy-v2/i18n';
import { ClpPrimaryCtaButton } from '@/features/course-landing-page/components/clp-primary-cta-button/clp-primary-cta-button.react-component';
import { GwgEnrollmentCTA } from '@udemy-v2/career-accelerators';
import { GwgSubscriptionCTA, GwgCheckoutProvider } from '@udemy-v2/react-subscription-browse';
import { useGwgPlanPreference } from '@/features/course-landing-page/hooks/use-gwg-plan-preference';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { useCourseCareerAccelerator } from '@/features/course-landing-page/context/career-accelerator-provider.react-component';
import { useClpSubscriptionContext } from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import { useClpPurchaseContext } from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import { SubscriptionPlanSubscriberState } from '@udemy-v2/subscription-graphql';
import styles from './grow-with-google-sticky-footer-content.module.scss';

/**
 * GrowWithGoogleStickyFooterContent component displays the CTA button and billing information
 * for Grow with Google subscription enrollment in the sticky footer.
 * Renders when the course is included in the Grow with Google subscription.
 */
export const GrowWithGoogleStickyFooterContent: React.FC = () => {
  const $ = _c(24);
  const {
    gettext
  } = useI18n();
  const course = useClpCourse();
  const {
    subscribedConsumerSubscriptionPlan
  } = useClpSubscriptionContext();
  const {
    gwgPlan,
    gwgPlusPersonalPlan,
    isFeaturesLoading,
    isMonthlyPersonalPlanAvailable
  } = useClpPurchaseContext();
  const courseCareerAccelerator = useCourseCareerAccelerator();
  const gwgPlanPreference = useGwgPlanPreference();
  const plan = gwgPlan?.isLoading && gwgPlusPersonalPlan?.isLoading === false ? undefined : gwgPlan;
  const isGwgSubscriber = gwgPlan && "isSubscriber" in gwgPlan && gwgPlan?.isSubscriber || gwgPlusPersonalPlan && "isSubscriber" in gwgPlusPersonalPlan && gwgPlusPersonalPlan?.isSubscriber;
  let t0;
  if ($[0] !== subscribedConsumerSubscriptionPlan) {
    t0 = subscribedConsumerSubscriptionPlan && subscribedConsumerSubscriptionPlan.subscribedPlan.productType ? {
      isSubscriber: true,
      isLoading: false,
      productType: subscribedConsumerSubscriptionPlan.subscribedPlan.productType
    } as SubscriptionPlanSubscriberState : undefined;
    $[0] = subscribedConsumerSubscriptionPlan;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = parseInt(course.id, 10);
  let t2;
  if ($[2] !== gettext || $[3] !== isFeaturesLoading || $[4] !== isMonthlyPersonalPlanAvailable) {
    t2 = isFeaturesLoading ? undefined : isMonthlyPersonalPlanAvailable ? gettext("Billed monthly or annually. Cancel anytime.") : gettext("Cancel anytime.");
    $[2] = gettext;
    $[3] = isFeaturesLoading;
    $[4] = isMonthlyPersonalPlanAvailable;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== course.trackingId || $[7] !== gwgPlusPersonalPlan || $[8] !== plan || $[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = <GwgSubscriptionCTA plan={plan} currentlySubscribedPlan={t0} combinedGwgPlan={gwgPlusPersonalPlan} courseId={t1} courseTrackingId={course.trackingId} uiRegion="sticky_footer" offerCtaStyle="primary" className={styles["cta-button"]} subtitleClassName={styles["billing-text"]} planPeriodText={t2} />;
    $[6] = course.trackingId;
    $[7] = gwgPlusPersonalPlan;
    $[8] = plan;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== gwgPlanPreference || $[14] !== t3) {
    t4 = <GwgCheckoutProvider planPreference={gwgPlanPreference}>{t3}</GwgCheckoutProvider>;
    $[13] = gwgPlanPreference;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let content = t4;
  if (isGwgSubscriber) {
    if (courseCareerAccelerator && !course.isEnrolled) {
      const t5 = Number(courseCareerAccelerator.id);
      let t6;
      if ($[16] !== gettext) {
        t6 = gettext("You will be enrolled in all courses included in this path.");
        $[16] = gettext;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      let t7;
      if ($[18] !== t5 || $[19] !== t6) {
        t7 = <GwgEnrollmentCTA udStyle="primary" size="large" className={styles["cta-button"]} acceleratorId={t5} unEnrolledSubtitle={t6} />;
        $[18] = t5;
        $[19] = t6;
        $[20] = t7;
      } else {
        t7 = $[20];
      }
      content = t7;
    } else {
      let t5;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = <ClpPrimaryCtaButton className={styles["cta-button"]} />;
        $[21] = t5;
      } else {
        t5 = $[21];
      }
      content = t5;
    }
  }
  let t5;
  if ($[22] !== content) {
    t5 = <div className={styles.container}><div className={styles["cta-wrapper"]}>{content}</div></div>;
    $[22] = content;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};